import { z } from "zod";

/** Modalità di invio delle email verso i contatti del tenant. */
export const TENANT_EMAIL_MODES = ["platform", "custom_smtp"] as const;
export type TenantEmailMode = (typeof TENANT_EMAIL_MODES)[number];

/** Esito di una verifica DNS (SPF / DKIM / DMARC). */
export const DNS_CHECK_STATUSES = ["unknown", "pass", "fail"] as const;
export type DnsCheckStatus = (typeof DNS_CHECK_STATUSES)[number];

/**
 * Salvataggio della configurazione email del tenant.
 * `smtpPass` assente = mantieni la password già salvata (mai rimandata al client).
 */
export const updateTenantEmailSettingsSchema = z
  .object({
    mode: z.enum(TENANT_EMAIL_MODES),
    fromName: z.string().trim().min(1).max(120),
    fromEmail: z.string().trim().toLowerCase().email().max(254),
    replyTo: z
      .string()
      .trim()
      .toLowerCase()
      .email()
      .max(254)
      .optional()
      .or(z.literal("").transform(() => undefined)),
    smtpHost: z.string().trim().min(1).max(253).optional(),
    smtpPort: z.coerce.number().int().min(1).max(65535).optional(),
    smtpSecure: z.boolean().optional(),
    smtpUser: z.string().trim().max(254).optional(),
    smtpPass: z.string().min(1).max(1024).optional(),
    rateLimit: z.coerce.number().int().min(1).max(100).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.mode === "custom_smtp") {
      if (!val.smtpHost) {
        ctx.addIssue({ code: "custom", path: ["smtpHost"], message: "smtp_host_required" });
      }
      if (!val.smtpPort) {
        ctx.addIssue({ code: "custom", path: ["smtpPort"], message: "smtp_port_required" });
      }
    }
  });
export type UpdateTenantEmailSettingsInput = z.infer<typeof updateTenantEmailSettingsSchema>;

export const testTenantEmailSchema = z.object({
  to: z.string().trim().toLowerCase().email().max(254),
});

/** Record DNS che il tenant deve pubblicare (modalità platform). */
export interface TenantDnsRecord {
  kind: "spf" | "dkim" | "dmarc";
  /** TXT (SPF/DMARC e DKIM app-managed) o CNAME (Easy DKIM di SES). */
  type: "TXT" | "CNAME";
  host: string;
  value: string;
  status: DnsCheckStatus;
  /** SPF: un record già esistente va integrato, non sostituito. */
  note?: string;
}
